import { AthosSearchProvider } from 'libs/athos/fetchers/search'
import { BeaconAnalyticsProvider, useBeaconLogRender, useBeaconLogImpressions } from 'libs/athos/hooks/useBeaconAnalytics'

import clsx from 'clsx'

import { useEffect, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/athos/hooks/useScrollCache'
import { useFilterScroll } from 'libs/athos/hooks/useFilterScroll'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { usePagedResults } from 'libs/athos/hooks/usePagedResults'
import { hasSeenResultPeek } from 'libs/athos/helpers/peek'
import { Pagination } from 'libs/athos/components/collection/Pagination'
import { useOption } from 'libs/athos/hooks/useOption'
import { FiltersProvider } from 'libs/athos/hooks/useFilters'
import { CollectionToolbar } from 'libs/athos/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/athos/components/collection/filters/FilterDrawer'
import { getDefaultGridCols } from 'libs/athos/components/collection/filters/GridLayoutToggle'
import { SearchHeader } from 'libs/athos/components/SearchHeader'
import { ResultItem } from 'libs/athos/components/ResultCardWrapper'
import { getPriorityCardCount, getGridImageSizes } from 'libs/athos/helpers/grid-priority'
import { SkeletonCard } from 'libs/athos/components/SkeletonCard'
import { calculateProductCount } from 'libs/athos/components/collection/filters/ProductCount'

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const [gridCols, setGridCols] = useState(getDefaultGridCols)
  const [gridNode, setGridNode] = useState(null)
  const { pageResults, isLoading, results } = usePagedResults()
  const { setScrollElement } = useFilterScroll()

  // Only the first grid row can be on screen at first paint — it gets real image
  // sources immediately, the rest stay lazy. See helpers/grid-priority.
  const priorityCount = getPriorityCardCount(gridCols)
  const imageSizes = getGridImageSizes(gridCols)

  // Beacon 2.0 search events; clickthrough fires from the result cards.
  useBeaconLogRender()
  useBeaconLogImpressions(gridNode)
  const query = useOption(
    'query',
    { syncWithURL: true }
  )

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && pageResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, pageResults?.length, firstLoad])

  useScrollCache([pageResults, firstLoad])

  const totalResults = calculateProductCount(results)

  return (
    <div className="grow flex flex-col">
      <SearchHeader query={query} totalResults={totalResults} isLoading={isLoading} corrections={corrections} results={results}/>
      <FiltersProvider>
        <CollectionToolbar gridCols={gridCols} setGridCols={setGridCols} />
        <div className="grow flex flex-col md:grid md:grid-cols-min-full">
          <FilterDrawer />
          <div className="grow flex flex-col">
            <div
              ref={(el) => { setScrollElement(el); setGridNode(el) }}
              className={clsx('grid grid-cols-2 md:grid-cols-3 [grid-auto-flow:row_dense] gap-x-gutter gap-y-0.25 w-full', gridCols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4', (isLoading && firstLoad) && 'min-h-screen-height')}
            >
              {showSkeleton && (firstLoad || isLoading || pageResults.length > 0) ? (
                Array.from({ length: window.settings?.per_page || 24 }, (_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                ))
              ) : (
                pageResults?.map((item, index) => {
                  return <ResultItem item={item} key={item.handle} dataIndex={index.toString()} peek={index === 0 && !hasSeenResultPeek()} priority={index < priorityCount} imageSizes={imageSizes} />
                })
              )}
            </div>
            {pageResults.length > 0 && !firstLoad && results?.pagination?.totalPages > 1 && (
              <div className="flex justify-center px-margin py-3.5">
                <Pagination />
              </div>
            )}
          </div>
        </div>
      </FiltersProvider>
      <x-back-to-top>Back To Top</x-back-to-top>
    </div>
  )
}

export function WrappedSearchGrid() {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  return (
    <AthosSearchProvider query={queryParam}>
      <BeaconAnalyticsProvider type="search">
        <SearchGrid />
      </BeaconAnalyticsProvider>
    </AthosSearchProvider>
  )
}
